import { useLanguage } from '../context/LanguageContext';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader, XCircle, Package } from 'lucide-react';
import { publicApi } from '../context/AuthContext';

export default function CheckoutSuccess() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [orderData, setOrderData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setErrorMessage('Session de paiement introuvable');
      return;
    }

    pollPaymentStatus(sessionId, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts) => {
    const maxAttempts = 8;
    const pollInterval = 2000; // 2 secondes

    if (attempts >= maxAttempts) {
      setStatus('error');
      setErrorMessage('Délai de vérification du paiement dépassé. Veuillez contacter le support.');
      return;
    }

    try {
      const { data } = await publicApi.get(`/payments/checkout/status/${sessionId}`);
      
      if (data.payment_status === 'paid') {
        setStatus('success');
        setOrderData(data);
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        setErrorMessage('Session de paiement expirée. Veuillez réessayer.');
        return;
      }

      // Si toujours en attente, continuer le polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Erreur vérification paiement:', error);
      setStatus('error');
      setErrorMessage('Erreur lors de la vérification du paiement. Veuillez réessayer.');
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-[#f5f7fa] flex items-center justify-center px-4">
        <div className="text-center">
          <Loader size={48} className="mx-auto mb-4 text-[#00aaff] animate-spin" />
          <h2 className="text-2xl font-black text-gray-900 mb-2">Vérification du paiement...</h2>
          <p className="text-gray-500">Veuillez patienter quelques instants</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-[#f5f7fa] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl p-8 text-center border border-gray-100">
          <XCircle size={64} className="mx-auto mb-4 text-red-500" />
          <h2 className="text-2xl font-black text-gray-900 mb-2">Erreur de paiement</h2>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <button
            onClick={() => navigate('/checkout')}
            className="w-full bg-[#00aaff] text-white font-bold py-3 rounded-full hover:bg-[#0088dd] transition-colors"
          >
            Retour au panier
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f7fa] flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-2xl p-8 text-center border border-gray-100">
        <CheckCircle size={64} className="mx-auto mb-4 text-green-500" />
        <h2 className="text-2xl font-black text-gray-900 mb-2">Paiement réussi !</h2>
        <p className="text-gray-600 mb-4">Votre commande a été confirmée avec succès</p>
        
        {(orderData?.order_number || orderData?.order_id) && (
          <div className="bg-blue-50 rounded-xl p-4 mb-6">
            <p className="text-sm text-gray-600 mb-1">Numéro de commande</p>
            <p className="text-lg font-bold text-[#00aaff]">{orderData.order_number || orderData.order_id}</p>
          </div>
        )}

        <p className="text-sm text-gray-500 mb-6">
          Vous recevrez un email de confirmation dans quelques instants
        </p>

        <div className="flex flex-col gap-3">
          <button
            onClick={() => navigate('/my-orders')}
            className="w-full bg-[#00aaff] text-white font-bold py-3 rounded-full hover:bg-[#0088dd] transition-colors flex items-center justify-center gap-2"
          >
            <Package size={18} />
            {t('checkoutSuccess.viewOrders')}
          </button>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-gray-100 text-gray-700 font-bold py-3 rounded-full hover:bg-gray-200 transition-colors"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    </div>
  );
}
